import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import ApprovalOutlinedIcon from '@mui/icons-material/ApprovalOutlined';
import { useAuthStore } from '../../auth/authStore';

/** Which product this booking belongs to — drives the label + where "onaya gönder" lands. */
export type ApprovalProduct = 'Flight' | 'Otel' | 'Transfer' | 'RentCar' | 'Bus';

const RESERVATION_PATH: Record<ApprovalProduct, string> = {
  Flight: '/air/reservations',
  Otel: '/hotel/reservations',
  Transfer: '/transfer/reservations',
  RentCar: '/rent-car/reservations',
  Bus: '/bus/reservations',
};

/** Structural subset of every product's cart shape that this panel reads. */
export interface ApprovableCart {
  id?: string;
  policySendApproval?: boolean;
  isOutBudget?: boolean;
  approvalStatus?: string;
  [key: string]: unknown;
}

/** Whether this cart needs manager approval before it can be paid: a CORPORATE user
 *  whose cart is flagged out-of-policy / over-budget. */
export function needsApproval(cart: ApprovableCart | null | undefined, gsaType?: string): boolean {
  if (gsaType !== 'CORPORATE' || !cart) return false;
  return (
    cart.policySendApproval === true ||
    cart.isOutBudget === true ||
    cart.approvalStatus === 'Pending'
  );
}

/**
 * "Onaya Gönder" — the corporate requester side. There is NO dedicated API endpoint:
 * the legacy web action just ran the normal UpdatePassengers → Billing/Submit →
 * MakePrebooking sequence (all real routes, and by the payment step the booking flow
 * has already done exactly that) and notified the manager. So this is purely a
 * client-side "stop here, don't pay" action — the prebooked over-budget corporate cart
 * is picked up by the approver's `Pending{X}` queue (see modules/corporate/approvals).
 *
 * NOTE (Air, 2026-09-08): when the cart has an `EmployeeID`, the Air flow handles this
 * EARLIER at the passenger step (`modules/air/components/AirApprovalPassengerStep`) with
 * read-only employee-sourced passenger data — this payment-step panel is the fallback for
 * over-budget carts with no `EmployeeID` (agency user entered passengers manually).
 */
export function SendForApprovalPanel({
  product,
  cart,
}: {
  product: ApprovalProduct;
  cart: ApprovableCart | null | undefined;
}) {
  const gsaType = useAuthStore((s) => s.currentUser?.gsaType);
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  if (!needsApproval(cart, gsaType)) return null;

  const confirm = () => {
    enqueueSnackbar('Rezervasyon onaya gönderildi. Yöneticiniz onayladığında ödeme yapabilirsiniz.', {
      variant: 'success',
    });
    setOpen(false);
    navigate(RESERVATION_PATH[product]);
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Alert severity="warning" sx={{ mb: 1 }}>
        Bu rezervasyon seyahat politikanız dışında veya bütçe üstünde. Ödeme yerine yöneticinizin onayına
        gönderilir; onaylanınca rezervasyon listenizden ödemeye devam edebilirsiniz.
      </Alert>
      <Button
        variant="contained"
        color="warning"
        startIcon={<ApprovalOutlinedIcon />}
        onClick={() => setOpen(true)}
      >
        Onaya Gönder
      </Button>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Onaya Gönder</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ pt: 1 }}>
            Rezervasyon ön rezervasyon olarak tutulur ve yöneticinizin onay listesine düşer. Devam etmek
            istiyor musunuz?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Vazgeç</Button>
          <Button variant="contained" onClick={confirm}>
            Onaya Gönder
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
